import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ElMessageBox } from 'element-plus'
import useAppStore from '@views/app.store'
import useMainStore from '@views/main/main.store'

type WsStatus = 'init' | 'connecting' | 'connected' | 'disconnected'

export default defineStore('websocket', () => {
  const appStore = useAppStore()
  const mainStore = useMainStore()

  const wsInstance = ref<WebSocket | null>(null) // WebSocket实例
  const status = ref<WsStatus>('init') // 连接状态: init/connecting/connected/disconnected
  const reconnectCount = ref(0) // 重连计数器（指数退避算法）
  const heartbeatTimer = ref<number | null>(null) // 心跳检测定时器ID
  const reconnectTimer = ref<number | null>(null) // 重连定时器ID
  // const msgDispatcher = ref<Map<string, any>>(new Map()) // 消息分发器

  // 指数退避重连
  const scheduleReconnect = () => {
    if (reconnectTimer.value) clearTimeout(reconnectTimer.value)

    const delay = Math.min(1000 * 2 ** reconnectCount.value, 30000)
    reconnectTimer.value = window.setTimeout(() => {
      reconnectCount.value += 1
      // eslint-disable-next-line no-use-before-define
      initWs()
    }, delay)
  }
  // 初始化连接
  const initWs = () => {
    if (status.value === 'connecting' || status.value === 'connected') return
    const accountId = appStore.userInfo?.id // 根据实际存储方式调整
    if (!accountId) {
      setTimeout(() => {
        initWs()
      }, 1000)
      return
    }
    status.value = 'connecting'
    wsInstance.value = new WebSocket(
      `${process.env.VUE_APP_WS_API}/tcloud-geo/websocket/${accountId}`,
      // `ws://192.168.26.15:8080/tcloud-geo/websocket/${accountId}`, // 毛金凯
      // `ws://192.168.26.214:8080/tcloud-geo/websocket/${accountId}`, // 任义桦
    )

    wsInstance.value.onopen = () => {
      status.value = 'connected'
      reconnectCount.value = 0
      // eslint-disable-next-line no-use-before-define
      startHeartbeat()
    }

    wsInstance.value.onmessage = event => {
      // eslint-disable-next-line no-use-before-define
      handleMessage(event.data)
    }

    wsInstance.value.onclose = event => {
      status.value = 'disconnected'
      if (!event.wasClean) {
        scheduleReconnect()
      }
    }

    wsInstance.value.onerror = error => {
      console.error('WebSocket error:', error)
      wsInstance.value?.close()
    }
  }

  // 心跳检测
  const startHeartbeat = () => {
    if (heartbeatTimer.value) clearInterval(heartbeatTimer.value)

    heartbeatTimer.value = window.setInterval(() => {
      if (wsInstance.value?.readyState === WebSocket.OPEN) {
        wsInstance.value.send(JSON.stringify({ type: 'heartbeat' }))
      }
    }, 30000)
  }

  // 消息处理
  const handleMessage = async (data: string) => {
    try {
      let info = JSON.parse(data)
      if (!info) return

      // 统一为数组格式处理
      if (!Array.isArray(info)) {
        info = [info]
      }

      // 心跳消息：数组内第一条的 socketType 或 type
      if (info[0]?.type === 'heartbeat' || info[0]?.socketType === 'heartbeat') {
        return
      }
      if (info.some(x => x.socketType === 'close')) {
        initWs()
        return
      }
      if (info.some(x => x.socketType === 'force_close')) {
        ElMessageBox.confirm('消息链接已断开，是否重新连接？', '提示', {
          confirmButtonText: '刷新重连',
          cancelButtonText: '取消',
          type: 'warning',
        })
          .then(() => {
            window.location.reload()
          })
          .catch(() => {
            console.log('取消')
          })
        return
      }

      for (let i = 0; i < info.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await mainStore.handleWsConversation(info[i])
      }
    } catch (error) {
      console.error('Message parse error:', error)
    }
  }

  // 清理资源
  const cleanup = () => {
    if (heartbeatTimer.value) clearInterval(heartbeatTimer.value)
    if (reconnectTimer.value) clearTimeout(reconnectTimer.value)
    heartbeatTimer.value = null
    reconnectTimer.value = null
    wsInstance.value = null
    status.value = 'init'
  }
  // 主动关闭连接
  const disconnect = () => {
    if (wsInstance.value) {
      wsInstance.value.close(1000, 'Normal closure')
      cleanup()
    }
  }

  return {
    wsInstance,
    status,
    // msgDispatcher,
    initWs,
    disconnect,
  }
})
